import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { FilterChip } from '@/components/shared/filter-chip';
import { useMerchantMap } from '@/hooks/use-merchant-map';
import type {
  IqServiceBookingType,
  IqServiceFlowType,
  IqServiceInventoryStatus,
  PlatformIqInventoryFilters,
  PlatformIqInventoryRow,
} from '@/types/iq-service-inventory';

/**
 * Spec 384 §2 — client-seitige Filter-Leiste der Plattform-Übersicht. Visuell am
 * #352-`service-filter-bar` orientiert (role="search", beschriftete Controls, entfernbare
 * Chips, Reset), aber bewusst IN-NAMESPACE und OHNE Netz-Aufruf: die Merchant-Facette zieht
 * ihre Optionen aus den bereits geladenen Zeilen (`merchantIds`) und löst sie über
 * `useMerchantMap` auf — kein `useMerchants`-Fetch (keine Read-Amplification, keine Kopplung
 * an den `services`-i18n-Namespace via `CustomerFilter`).
 *
 * Die Status-Facette dient zugleich als Status-Legende (alle Werte sichtbar).
 */

const STATUS_VALUES: IqServiceInventoryStatus[] = ['active', 'pending_provisioning', 'archived'];
const FLOW_VALUES: IqServiceFlowType[] = ['WAP_CLICK', 'WEB_TAN'];
const BOOKING_VALUES: IqServiceBookingType[] = ['SUBSCRIPTION', 'OTP'];

const ALL = '__all__';

/**
 * Facet options = the known enum legend (always shown, even if absent from the rows) UNION
 * any forward-compatible raw value actually present in the loaded rows. Without the union a
 * Facade-added value the page renders verbatim (Spec 384 §1) would be unfilterable. Known
 * values get their i18n label; an unknown present value is its own label (raw, verbatim).
 */
function facetOptions(
  known: readonly string[],
  present: Array<string | undefined>,
  labelFor: (value: string) => string,
): Array<{ value: string; label: string }> {
  const seen = new Set<string>(known);
  const options = known.map((value) => ({ value, label: labelFor(value) }));
  for (const value of present) {
    if (value && !seen.has(value)) {
      seen.add(value);
      options.push({ value, label: value });
    }
  }
  return options;
}

interface PlatformIqInventoryFilterBarProps {
  filters: PlatformIqInventoryFilters;
  /** Die geladenen Zeilen — Facetten-Quelle (Merchant + present-Enum-Werte), kein Fetch. */
  rows: PlatformIqInventoryRow[];
  hasActiveFilters: boolean;
  onChange: (patch: Partial<PlatformIqInventoryFilters>) => void;
  onReset: () => void;
}

export function PlatformIqInventoryFilterBar({
  filters,
  rows,
  hasActiveFilters,
  onChange,
  onReset,
}: PlatformIqInventoryFilterBarProps) {
  const { t } = useTranslation('iq-service-inventory');
  const { resolveMerchantName, resolveCustomerId } = useMerchantMap();

  // Distinkte merchantIds der geladenen Zeilen (kein Fetch — keine Read-Amplification).
  const merchantIds = useMemo(() => [...new Set(rows.map((r) => r.merchantId))], [rows]);

  // Merchant-Optionen aus den geladenen Zeilen — Name als Label, Kundennummer als
  // durchsuchbare `description` (die Combobox indiziert label+value+description).
  const merchantOptions = useMemo<ComboboxOption[]>(() => {
    return merchantIds
      .map((id) => {
        const customerId = resolveCustomerId(id);
        return {
          value: id,
          label: resolveMerchantName(id),
          ...(customerId ? { description: customerId } : {}),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [merchantIds, resolveMerchantName, resolveCustomerId]);

  // Status/Flow/Booking-Optionen: bekannte Enum-Legende ∪ present forward-compat-Werte.
  const statusOptions = useMemo(
    () => facetOptions(STATUS_VALUES, rows.map((r) => r.status), (v) => t(`status.${v}`)),
    [rows, t],
  );
  const flowOptions = useMemo(
    () => facetOptions(FLOW_VALUES, rows.map((r) => r.flowType), (v) => t(`flowType.${v}`)),
    [rows, t],
  );
  const bookingOptions = useMemo(
    () => facetOptions(BOOKING_VALUES, rows.map((r) => r.bookingType), (v) => t(`bookingType.${v}`)),
    [rows, t],
  );

  const statusLabel = t('filters.status');
  const flowLabel = t('filters.flowType');
  const bookingLabel = t('filters.bookingType');
  const merchantLabel = t('overview.filter.merchantLabel');

  return (
    <div className="mb-4 space-y-3" role="search">
      <div className="flex flex-wrap items-end gap-3">
        {/* Freitext — Kennung ODER Name. */}
        <div>
          <Label htmlFor="iq-overview-search" className="text-xs text-muted-foreground">
            {t('overview.filter.search')}
          </Label>
          <Input
            id="iq-overview-search"
            value={filters.text ?? ''}
            onChange={(e) => onChange({ text: e.target.value || undefined })}
            placeholder={t('overview.filter.searchPlaceholder')}
            className="w-full sm:w-56"
          />
        </div>

        {merchantIds.length > 1 && (
          <div>
            <Label className="text-xs text-muted-foreground">{merchantLabel}</Label>
            <Combobox
              options={merchantOptions}
              value={filters.merchantId ?? ''}
              onChange={(v) => onChange({ merchantId: v || undefined })}
              placeholder={t('overview.filter.merchantPlaceholder')}
              searchPlaceholder={t('overview.filter.merchantSearch')}
              emptyText={t('overview.filter.merchantEmpty')}
              className="w-full sm:w-72"
            />
          </div>
        )}

        <FacetSelect
          label={statusLabel}
          value={filters.status}
          onValueChange={(v) => onChange({ status: v as IqServiceInventoryStatus | undefined })}
          options={statusOptions}
          allLabel={t('filters.all')}
        />
        <FacetSelect
          label={flowLabel}
          value={filters.flowType}
          onValueChange={(v) => onChange({ flowType: v as IqServiceFlowType | undefined })}
          options={flowOptions}
          allLabel={t('filters.all')}
        />
        <FacetSelect
          label={bookingLabel}
          value={filters.bookingType}
          onValueChange={(v) => onChange({ bookingType: v as IqServiceBookingType | undefined })}
          options={bookingOptions}
          allLabel={t('filters.all')}
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 text-muted-foreground"
            onClick={onReset}
          >
            <X className="mr-1.5 h-4 w-4" />
            {t('overview.filter.reset')}
          </Button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {filters.text?.trim() && (
            <FilterChip
              label={`${t('overview.filter.search')}: ${filters.text}`}
              onRemove={() => onChange({ text: undefined })}
              removeLabel={t('overview.filter.chipRemove')}
              maxLabelWidth="220px"
            />
          )}
          {filters.merchantId && (
            <FilterChip
              label={`${merchantLabel}: ${resolveMerchantName(filters.merchantId)}`}
              onRemove={() => onChange({ merchantId: undefined })}
              removeLabel={t('overview.filter.chipRemove')}
              maxLabelWidth="220px"
            />
          )}
          {filters.status && (
            <FilterChip
              label={`${statusLabel}: ${labelOf(statusOptions, filters.status)}`}
              onRemove={() => onChange({ status: undefined })}
              removeLabel={t('overview.filter.chipRemove')}
              maxLabelWidth="220px"
            />
          )}
          {filters.flowType && (
            <FilterChip
              label={`${flowLabel}: ${labelOf(flowOptions, filters.flowType)}`}
              onRemove={() => onChange({ flowType: undefined })}
              removeLabel={t('overview.filter.chipRemove')}
              maxLabelWidth="220px"
            />
          )}
          {filters.bookingType && (
            <FilterChip
              label={`${bookingLabel}: ${labelOf(bookingOptions, filters.bookingType)}`}
              onRemove={() => onChange({ bookingType: undefined })}
              removeLabel={t('overview.filter.chipRemove')}
              maxLabelWidth="220px"
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Resolve a facet value to its display label (falls back to the raw value). */
function labelOf(options: Array<{ value: string; label: string }>, value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

function FacetSelect({
  label,
  value,
  onValueChange,
  options,
  allLabel,
}: {
  label: string;
  value: string | undefined;
  onValueChange: (value: string | undefined) => void;
  options: Array<{ value: string; label: string }>;
  allLabel: string;
}) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select
        value={value ?? ALL}
        onValueChange={(v) => onValueChange(v === ALL ? undefined : v)}
      >
        <SelectTrigger className="w-[150px]" aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
